import { Checkbox } from '@ardoq/forms';
import { ChevronIconName, Icon, IconName, IconSize } from '@ardoq/icons';
import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { IconButton, GhostButton, UnstyledButton } from '@ardoq/button';
import { colors, s16 } from '@ardoq/design-tokens';
import { InlineText } from '@ardoq/typography';
import { ordinalSuffixOf } from '@ardoq/common-helpers';
import { noop } from 'lodash';
import { ViewError } from '@ardoq/graph';
import { GroupType } from '@ardoq/api-types';
import { FlexBox, Stack } from '@ardoq/layout';

const defaultGroupTypeNames: Record<GroupType, string> = {
  [GroupType.CHILD]: 'Group by child',
  [GroupType.COMPONENT]: 'Group by component type',
  [GroupType.FIELD]: 'Group by field value',
  [GroupType.PARENT]: 'Group by parent',
  [GroupType.PARENT_ALL]: 'Group by parent all',
  [GroupType.REFERENCE]: 'Group by reference type',
  [GroupType.REFERENCE_CONTEXTUAL]: 'Group by reference contextual type',
  [GroupType.REFERENCE_TYPE_NAME]: 'Group by reference type name',
  [GroupType.TAG]: 'Group by tag',
  [GroupType.WORKSPACE]: 'Group by workspace',
  [GroupType.ALL]: 'Group all',
};

export type GetRuleErrorPrefixArgs = {
  ordinal: string;
  ruleTypeName: string;
};

const defaultGetRuleErrorPrefix = ({
  ordinal,
  ruleTypeName,
}: GetRuleErrorPrefixArgs): string =>
  `The ${ordinal} rule, ${ruleTypeName}, has the following problem:`;

const ErrorInfoContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  left: ${s16};
  width: 296px;
  overflow: auto;
  position: absolute;
  top: 0;
  z-index: 5;
  padding-right: 1px;
  padding-left: 1px;

  .forExport & {
    display: none;
  }
`;

type ErrorEntryProps = ViewError & {
  detailsLabel: string;
  groupTypeNames: Record<GroupType, string>;
  getRuleErrorPrefix: (args: GetRuleErrorPrefixArgs) => string;
};

const ErrorEntry = ({
  message,
  rule,
  index,
  details,
  buttonLabel,
  onButtonClick,
  detailsLabel,
  groupTypeNames,
  getRuleErrorPrefix,
}: ErrorEntryProps) => {
  return (
    <Stack gap="medium">
      {rule && (
        <div>
          {getRuleErrorPrefix({
            ordinal: ordinalSuffixOf(index + 1),
            ruleTypeName: groupTypeNames[rule.type],
          })}
        </div>
      )}
      <div>{message}</div>
      {details && (
        <ErrorDetails details={details} detailsLabel={detailsLabel} />
      )}
      {buttonLabel && (
        <FlexBox justify="flex-end">
          <GhostButton onClick={onButtonClick}>{buttonLabel}</GhostButton>
        </FlexBox>
      )}
    </Stack>
  );
};

const ErrorDetails = ({
  details,
  detailsLabel,
}: {
  details: string[];
  detailsLabel: string;
}) => {
  const [showDetails, setShowDetails] = useState<boolean>(false);
  return (
    <UnstyledButton onClick={() => setShowDetails(!showDetails)}>
      <FlexBox align="center" style={{ cursor: 'pointer' }}>
        <InlineText variant="text2Bold">{detailsLabel}</InlineText>
        <Icon
          iconSize={IconSize.MEDIUM}
          iconName={showDetails ? ChevronIconName.UP : ChevronIconName.DOWN}
        />
      </FlexBox>
      {showDetails && (
        <FlexBox marginTop="small" paddingY="small">
          <Stack gap="small">
            {details.map((detail, index) => (
              <div key={`detail-${index}`}>{detail}</div>
            ))}
          </Stack>
        </FlexBox>
      )}
    </UnstyledButton>
  );
};

export type ErrorInfoBoxLabels = {
  detailsLabel?: string;
  closeButtonLabel?: string;
  cloneInfoTitle?: string;
  cloneInfoDescription?: string;
  neverShowAgainLabel?: string;
  rulesNotAppliedLabel?: string;
  groupTypeNames?: Record<GroupType, string>;
  getRuleErrorPrefix?: (args: GetRuleErrorPrefixArgs) => string;
};

type ErrorInfoBoxProps = ErrorInfoBoxLabels & {
  errors: ViewError[] | null;
  clearErrors: () => void;
  hasClones: boolean;
  clearHasClones: () => void;
  isShowNeverAgainSet: boolean;
  toggleNeverShowAgain: () => void;
  isPresentationMode: boolean;
  trackShowingGroupingErrorBox?: ({
    errorTypes,
    hasClones,
  }: {
    errorTypes: Record<GroupType, boolean>;
    hasClones: boolean;
  }) => void;
};

export const ErrorInfoBox = ({
  errors,
  clearErrors,
  hasClones,
  clearHasClones,
  isShowNeverAgainSet,
  toggleNeverShowAgain,
  isPresentationMode = false,
  trackShowingGroupingErrorBox = noop,
  detailsLabel = 'Details',
  closeButtonLabel = 'Close',
  cloneInfoTitle = 'Some of the grouping rules have created duplicate components.',
  cloneInfoDescription = 'This can be confusing at first, but it is normal for some grouping rules to result in duplicated components. Grouping by reference type or by parents are common grouping scenarios which often produce duplicates.',
  neverShowAgainLabel = 'Never show this message again',
  rulesNotAppliedLabel = 'These rules could not be applied:',
  groupTypeNames = defaultGroupTypeNames,
  getRuleErrorPrefix = defaultGetRuleErrorPrefix,
}: ErrorInfoBoxProps) => {
  const noErrors = (!errors || errors.length === 0) && !hasClones;
  useEffect(() => {
    if (noErrors) return;
    const errorTypes = Object.fromEntries(
      errors?.map(error => [error?.rule?.type, true]) || []
    ) as Record<GroupType, boolean>;
    trackShowingGroupingErrorBox({ errorTypes, hasClones });
  }, [errors, hasClones, noErrors, trackShowingGroupingErrorBox]);
  if (noErrors || isPresentationMode) {
    return null;
  }
  const hasRuleErrors = errors?.some(error => error.rule);
  return (
    <ErrorInfoContainer>
      {hasClones && (
        <FlexBox
          width="full"
          padding="medium"
          marginY="medium"
          borderRadius="r4"
          borderColor="borderAction"
          borderWidth={1}
          backgroundColor="surfaceStatusNeutral"
        >
          <Stack gap="medium" flex={1}>
            <FlexBox justify="space-between" align="center">
              <Icon iconName={IconName.INFO} color={colors.icon} />
              <FlexBox align="center">
                <IconButton
                  label={closeButtonLabel}
                  iconName={IconName.CLOSE}
                  onClick={clearHasClones}
                />
              </FlexBox>
            </FlexBox>
            <FlexBox>
              <Stack gap="medium">
                <div>{cloneInfoTitle}</div>
                <div>{cloneInfoDescription}</div>
                <div>
                  <Checkbox
                    isChecked={isShowNeverAgainSet}
                    onChange={toggleNeverShowAgain}
                  >
                    {neverShowAgainLabel}
                  </Checkbox>
                </div>
              </Stack>
            </FlexBox>
          </Stack>
        </FlexBox>
      )}
      {errors && errors.length > 0 && (
        <FlexBox
          width="full"
          padding="medium"
          marginY="medium"
          borderRadius="r4"
          borderColor="borderAction"
          borderWidth={1}
          backgroundColor="surfaceStatusNeutral"
        >
          <Stack gap="medium" flex={1}>
            <FlexBox justify="space-between" align="center">
              <Icon iconName={IconName.ERROR} color={colors.icon} />
              <FlexBox align="center">
                <IconButton
                  label={closeButtonLabel}
                  iconName={IconName.CLOSE}
                  onClick={clearErrors}
                />
              </FlexBox>
            </FlexBox>
            {hasRuleErrors && <div>{rulesNotAppliedLabel}</div>}
            {errors.map((error, index) => (
              <ErrorEntry
                key={`error-${index}`}
                {...error}
                detailsLabel={detailsLabel}
                groupTypeNames={groupTypeNames}
                getRuleErrorPrefix={getRuleErrorPrefix}
              />
            ))}
          </Stack>
        </FlexBox>
      )}
    </ErrorInfoContainer>
  );
};
